/** #847 Core/Edge continuity contract. Transport validation and replay feed
 * the per-source authority in sourceEvidence.ts.
 * Continuity is not inventory completeness or a bound on network delay.
 */
export type SourceLane = 'kalshi-ticker' | 'kalshi-orderbook' | 'poly-floor' | 'polyus-floor'
  | `${'kalshi-ticker' | 'kalshi-orderbook' | 'poly-book' | 'polyus-book'}:${string}`;
export type ContinuityState = 'unknown' | 'valid' | 'disconnected' | 'gap' | 'resyncing';
export interface SourceContinuity {
  type: 'sourceContinuity'; lane: SourceLane; sourceEpoch: string; sourceGen: number;
  state: ContinuityState; sinceMs: number; serverTs: number; seq: number;
  coverage: { claimed: number; current: number };
}
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const count = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const stamp = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const lanes: readonly string[] = ['kalshi-ticker', 'kalshi-orderbook', 'poly-floor', 'polyus-floor'];
const isLane = (v: unknown): v is SourceLane => typeof v === 'string'
  && (lanes.includes(v) || /^(kalshi-ticker|kalshi-orderbook|poly-book|polyus-book):[A-Z0-9_-]{1,32}$/.test(v));
const states: readonly string[] = ['unknown', 'valid', 'disconnected', 'gap', 'resyncing'];

export function parseSourceContinuity(raw: unknown): SourceContinuity | null {
  if (!record(raw) || raw.type !== 'sourceContinuity' || !isLane(raw.lane)
    || !states.includes(raw.state as string) || typeof raw.sourceEpoch !== 'string' || !raw.sourceEpoch.trim()
    || !count(raw.sourceGen) || !count(raw.seq) || !stamp(raw.sinceMs) || !stamp(raw.serverTs)
    || !record(raw.coverage) || !count(raw.coverage.claimed) || !count(raw.coverage.current)
    || raw.coverage.current > raw.coverage.claimed) return null;
  return { type: 'sourceContinuity', lane: raw.lane as SourceLane, state: raw.state as ContinuityState,
    sourceEpoch: raw.sourceEpoch, sourceGen: raw.sourceGen, sinceMs: raw.sinceMs,
    serverTs: raw.serverTs, seq: raw.seq,
    coverage: { claimed: raw.coverage.claimed, current: raw.coverage.current } };
}

export interface ContinuitySession { readonly authority: string }
/** Per connection, never a global lane registry across Core endpoints. Binding
 * an epoch is the adapter's authenticated-session decision, not row data's.
 */
export class SourceContinuityAdmission {
  private session: ContinuitySession | null = null;
  private epochs = new Map<SourceLane, string>();
  private lanes = new Map<SourceLane, SourceContinuity>();
  connect(authority: string): ContinuitySession {
    if (!authority.trim()) throw new Error('Continuity authority required');
    this.session = Object.freeze({ authority }); this.epochs.clear(); this.lanes.clear();
    return this.session;
  }
  disconnect(session: ContinuitySession): void {
    if (session === this.session) { this.session = null; this.epochs.clear(); this.lanes.clear(); }
  }
  bind(session: ContinuitySession, lane: SourceLane, epoch: string): boolean {
    if (session !== this.session || !epoch.trim() || this.epochs.has(lane)) return false;
    this.epochs.set(lane, epoch); return true;
  }
  accept(session: ContinuitySession, raw: unknown): boolean {
    if (session !== this.session) return false;
    const frame = parseSourceContinuity(raw);
    if (!frame || this.epochs.get(frame.lane) !== frame.sourceEpoch) return false;
    const prior = this.lanes.get(frame.lane);
    if (prior && (frame.seq <= prior.seq || frame.sourceGen < prior.sourceGen)) return false;
    this.lanes.set(frame.lane, frame); return true;
  }
  inspect(session: ContinuitySession, row: unknown): {
    verdict: 'unknown' | 'display-only' | 'continuous'; coverage: SourceContinuity['coverage'] | null;
  } {
    if (session !== this.session || !record(row) || !isLane(row.lane)
      || typeof row.sourceEpoch !== 'string' || !count(row.sourceGen)) return { verdict: 'unknown', coverage: null };
    const governing = this.lanes.get(row.lane as SourceLane);
    if (!governing) return { verdict: 'unknown', coverage: null };
    const continuous = governing.state === 'valid' && row.sourceEpoch === governing.sourceEpoch
      && row.sourceGen === governing.sourceGen && row.pendingResync !== true;
    return { verdict: continuous ? 'continuous' : 'display-only', coverage: { ...governing.coverage } };
  }
}

/** Connection-local transport cache. It preserves controls for late tabs;
 * it does not certify row age or change trading eligibility. A malformed
 * control or unexpected epoch change quarantines its lane until reset. Only
 * unidentifiable controls or exhausted lane capacity invalidate the connection,
 * so an old valid announcement cannot silently survive an unreadable gap.
 */
export class SourceContinuityTransport {
  private frames = new Map<SourceLane, SourceContinuity>();
  private quarantined = new Set<SourceLane>();
  private invalid = false;
  reset(): void { this.frames.clear(); this.quarantined.clear(); this.invalid = false; }
  quarantine(lane: unknown): SourceLane | undefined {
    if (!this.invalid && isLane(lane)
      && (this.frames.has(lane) || this.quarantined.has(lane) || this.frames.size + this.quarantined.size < 256)) {
      this.frames.delete(lane); this.quarantined.add(lane); return lane;
    }
    this.frames.clear(); this.quarantined.clear(); this.invalid = true; return undefined;
  }
  receive(raw: unknown): 'accepted' | 'ignored' | 'invalid' | 'quarantined' {
    if (this.invalid) return 'invalid';
    const lane = record(raw) && isLane(raw.lane) ? raw.lane : undefined;
    if (lane && this.quarantined.has(lane)) return 'quarantined';
    const frame = parseSourceContinuity(raw);
    const prior = frame ? this.frames.get(frame.lane) : undefined;
    if (!frame || (prior && prior.sourceEpoch !== frame.sourceEpoch)
      || (!prior && this.frames.size + this.quarantined.size >= 256)) {
      return this.quarantine(lane) ? 'quarantined' : 'invalid';
    }
    if (prior && (frame.seq <= prior.seq || frame.sourceGen < prior.sourceGen)) return 'ignored';
    this.frames.set(frame.lane, frame);
    return 'accepted';
  }
  snapshot(): SourceContinuity[] { return [...this.frames.values()]; }
  get(lane: SourceLane): SourceContinuity | undefined { return this.frames.get(lane); }
  get invalidated(): boolean { return this.invalid; }
  get quarantinedLanes(): SourceLane[] { return [...this.quarantined]; }
}
